import React, { useEffect, useState, useRef } from 'react';
import { normalizeSearch } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EventItem, Place } from '../../types/shared/event';
import { useHeader } from '../../context/HeaderContext';
import { SearchAndFilterBar } from '../standard/SearchAndFilterBar';
import { FilterDrawer } from '../shared/FilterDrawer';
import type { FilterState } from '../shared/FilterDrawer';
import { ListView } from './ListView';
import MapView from './MapView';
import { ViewToggle } from '../shared/ViewToggle';
import { DetailCard } from './DetailCard';
import { PlaceCard } from './PlaceCard';
import DateRangeSelector from '../event-api/DateRangeSelector';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { stayService } from '../../lib/stay/stayService';
import { navigationClient } from '../../lib/navigation';
import { eventClient } from '../../lib/event';
import { useAnalytics } from '../../hooks/useAnalytics';
import ErrorBoundary from '../analytics/ErrorBoundary';

const EventSection: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'event'
  });
  
  const [filters, setFilters] = useState<FilterState>({ category: '', categories: [], types: [], priceFilter: [] });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedItem, setSelectedItem] = useState<EventItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [detailBehavior, setDetailBehavior] = useState<'default' | 'pin' | 'place'>('default');
  const [selectedShowIndex, setSelectedShowIndex] = useState<number | null>(null);
  
  // Date range state for filtering
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchParams, setSearchParams] = useState<{
    startDateTime?: string;
    endDateTime?: string;
  } | null>(null);
  const [isDateRangeSelectorModalOpen, setIsDateRangeSelectorModalOpen] = useState(true);
  const [datesInitialized, setDatesInitialized] = useState(false);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Fetch items using the event client
  const { data: items = [], isLoading: itemsLoading, isPending: itemsPending, isError: itemsIsError, error: itemsError } = useQuery({
    queryKey: ['event-items', section?.id, searchParams],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found');
      }

      if (!searchParams) {
        return [];
      }

      const result = await eventClient.getEvents(
        section.id,
        searchParams?.startDateTime,
        searchParams?.endDateTime
      );

      return result;
    },
    enabled: !!section?.id && !!searchParams,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analytics.trackSectionView(view);
    }
  }, [section?.id, view, analytics]);

  // Category colors memo
  const categoryColors = React.useMemo(() => {
    if (!config || !items.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(items.map(item => item.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, items]);

  const activeCategories = React.useMemo(() =>
    filters.categories.length > 0 ? filters.categories : (filters.category ? [filters.category] : []),
    [filters.categories, filters.category]
  );

  const activeFiltersCount =
    activeCategories.length + filters.types.length + filters.priceFilter.length;

  const filteredItems = React.useMemo(() => {
    return items.filter(item => {
      const categoryMatch = activeCategories.length === 0 || activeCategories.includes(item.category);
      const typeMatch = filters.types.length === 0 || filters.types.includes(item.type);
      const priceMatch = filters.priceFilter.length === 0 || filters.priceFilter.includes(item.price?.trim() || '');
      const q = normalizeSearch(searchQuery.trim());
      const searchMatch =
        !q ||
        normalizeSearch(item.name).includes(q) ||
        normalizeSearch(item.category || '').includes(q) ||
        normalizeSearch(item.type || '').includes(q);
      return categoryMatch && typeMatch && priceMatch && searchMatch;
    });
  }, [items, filters, searchQuery, activeCategories]);

  // Places memo
  const places = React.useMemo(() => {
    return eventClient.getPlaces(filteredItems);
  }, [filteredItems]);

  const mainLocation = React.useMemo(() => ({
    name: config.name,
    latitude: config.latitude,
    longitude: config.longitude,
    fulladress: config.fulladress
  }), [config.name, config.latitude, config.longitude, config.fulladress]);

  const allCategories = React.useMemo(() =>
    Array.from(new Set(items.map(i => i.category))).filter(Boolean),
    [items]
  );

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  useEffect(() => {
    if (name && items.length > 0) {
      // Check if the name contains date and time information
      const parts = name.split('-');
      if (parts.length >= 3) {
        // The last two parts should be date and time
        const dateStr = parts[parts.length - 2];
        const timeStr = parts[parts.length - 1];
        
        // Try to find an event with matching name, date, and time
        const event = items.find(e => {
          const eventNameSlug = slugify(e.name);
          const eventDateSlug = e.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
          const eventTimeSlug = e.hour.replace(':', ''); // Convert HH:MM to HHMM
          
          // Check if the base name matches (excluding date and time parts)
          const baseNameSlug = parts.slice(0, parts.length - 2).join('-');
          
          return eventNameSlug.includes(baseNameSlug) && 
                 eventDateSlug === dateStr && 
                 eventTimeSlug === timeStr;
        });
        
        if (event) {
          setSelectedItem(event);
          
          // Track detail card view from URL navigation
          analytics.trackItemAction(event.id, 'item_click', 'event', {
            item_name: event.name,
            category: event.category,
            type: event.type,
            source: 'url_navigation',
            date: event.date,
            time: event.hour,
            show_id: event.idShow || event.idshow
          });
          
          return;
        }
      }
      
      // Fallback to just matching by name if the above doesn't work
      const event = items.find(i => slugify(i.name) === name);
      if (event) {
        setSelectedItem(event);
        
        // Track detail card view from URL navigation
        analytics.trackItemAction(event.id, 'item_click', 'event', {
          item_name: event.name,
          category: event.category,
          type: event.type,
          source: 'url_navigation',
          date: event.date,
          time: event.hour,
          show_id: event.idShow || event.idshow
        });
      }
    }
  }, [name, items, analytics]);

  // Initialize dates from stay service
  useEffect(() => {
    if (datesInitialized) return;

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 0);

    const savedDates = stayService.getStayDates();
    const datesExpired = stayService.areDatesExpired();

    if (savedDates && !datesExpired) {
      const start = new Date(savedDates.startDate);
      const end = new Date(savedDates.endDate);
      setStartDate(start);
      setEndDate(end);
      setSearchParams({
        startDateTime: savedDates.startDate,
        endDateTime: savedDates.endDate
      });
    } else {
      setStartDate(today);
      setEndDate(tomorrow);
      setSearchParams({
        startDateTime: today.toISOString(),
        endDateTime: tomorrow.toISOString()
      });
    }

    setIsDateRangeSelectorModalOpen(false);
    setDatesInitialized(true);
  }, [datesInitialized]);

  // Event handlers
  const handleBackClick = () => {
    analytics.trackEvent('navigation', 'menu_click', {
      target: 'home',
      section_id: section?.id,
      section_name: section?.names?.fr
    });
    
    navigate('/');
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    analytics.trackFilter('filter_change', JSON.stringify(newFilters));
    setFilters(newFilters);
    setSelectedItem(null);
    setSelectedPlace(null);
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleCategorySelect = (category: string) => {
    const current = activeCategories;
    const isAlready = current.includes(category);
    const next = isAlready ? current.filter(c => c !== category) : [...current, category];
    handleFiltersChange({ ...filters, category: next.length === 1 ? next[0] : '', categories: next, types: [] });
  };

  const handleTypeSelect = (type: string) => {
    handleFiltersChange({ ...filters, types: type ? [type] : [] });
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackEvent('view', 'view_change', {
      section_id: section?.id,
      section_name: section?.names?.fr,
      section_type: 'event',
      view_type: newView,
      previous_view: view
    });
    
    setView(newView);
  };

  // Create a unique URL for an event that includes date and time
  const createEventUrl = (event: EventItem): string => {
    if (!section?.names?.fr) return '';
    
    const sectionSlug = slugify(section.names.fr);
    const nameSlug = slugify(event.name);
    const dateSlug = event.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
    const timeSlug = event.hour.replace(':', ''); // Convert HH:MM to HHMM
    
    // Use relative navigation
    return `/${sectionSlug}/${nameSlug}-${dateSlug}-${timeSlug}`;
  };

  const handleItemClick = (item: EventItem) => {
    scrollPositionRef.current = window.scrollY;

    analytics.trackItemAction(item.id, 'item_click', 'event', {
      item_name: item.name,
      category: item.category,
      type: item.type,
      source: view === 'list' ? 'list_view' : 'map_view',
      date: item.date,
      time: item.hour,
      show_id: item.idShow || item.idshow
    });

    if (view === 'map' && mapInstanceRef.current && item.latitude && item.longitude) {
      mapInstanceRef.current.panTo({ lat: parseFloat(String(item.latitude)), lng: parseFloat(String(item.longitude)) });
      const currentZoom = mapInstanceRef.current.getZoom() || 12;
      if (currentZoom < 15) {
        mapInstanceRef.current.setZoom(15);
      }
    }

    const eventUrl = createEventUrl(item);
    navigate(eventUrl, { replace: true });

    setSelectedItem(item);
    setDetailBehavior('default');
    setSelectedPlace(null);
  };

  const handlePlaceClick = (place: Place) => {
    analytics.trackEvent('interaction', 'place_card_view', {
      section_id: section?.id,
      place_name: place.name,
      shows_count: place.shows.length,
      show_ids: place.shows.map(show => show.idShow || '').filter(Boolean)
    });
    
    setSelectedPlace(place);
    setSelectedItem(null);
  };

  const handleEventFromPlace = (event: EventItem, showIndex: number) => {
    if (!section?.names?.fr) return;

    analytics.trackItemAction(event.id, 'item_click', 'event', {
      item_name: event.name,
      category: event.category,
      type: event.type,
      source: 'place_card',
      place_name: selectedPlace?.name,
      show_index: showIndex,
      date: event.date,
      time: event.hour,
      show_id: event.idShow || event.idshow
    });
    
    const eventUrl = createEventUrl(event);
    navigate(eventUrl, { replace: true });
    
    setSelectedItem(event);
    setSelectedShowIndex(showIndex);
    setDetailBehavior('place');
  };

  const handleItemChange = (newItem: EventItem) => {
    if (!section?.names?.fr) return;
    
    analytics.trackItemAction(newItem.id, 'navigation_next', 'event', {
      item_name: newItem.name,
      category: newItem.category,
      type: newItem.type,
      previous_item_id: selectedItem?.id,
      previous_item_name: selectedItem?.name,
      date: newItem.date,
      time: newItem.hour,
      show_id: newItem.idShow || newItem.idshow
    });
    
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(newItem);
    
    if (detailBehavior === 'place' && selectedPlace) {
      // Find the new index in the shows
      const newIndex = selectedPlace.shows.findIndex(show => 
        show.events.some(e => e.id === newItem.id)
      );
      if (newIndex !== -1) {
        setSelectedItem(newItem);
        setSelectedShowIndex(newIndex);
      }
    } else {
      setSelectedItem(newItem);
    }
    navigate(eventUrl, { replace: true });
  };

  const handleDetailClose = () => {
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`, { replace: true });
    
    analytics.trackEvent('interaction', 'detail_card_close', {
      section_id: section?.id,
      item_id: selectedItem?.id,
      item_name: selectedItem?.name,
      view_duration: selectedItem ? Date.now() - scrollPositionRef.current : 0
    });
    
    setSelectedItem(null);
    setSelectedPlace(null);
    setSelectedShowIndex(null);
    setDetailBehavior('default');
    
    // Restore scroll position after a short delay to allow for navigation
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 50);
  };

  // Handle date search
  const handleSearch = () => {
    if (!startDate || !endDate) return;

    // For regular events, use local timezone - DO NOT CONVERT TO UTC
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // If start date is today, use current time
    const now = new Date();
    if (startDateTime.toDateString() === now.toDateString()) {
      startDateTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    } else {
      startDateTime.setHours(0, 0, 1);
    }

    // Make sure the end date includes the full day by setting it to 23:59:59
    endDateTime.setHours(23, 59, 59);

    // Save the dates to the stay service
    stayService.setStayDates(startDate, endDate);
    
    // Create strings in local timezone format (YYYY-MM-DDTHH:MM:SS)
    // This is critical - we need to keep the local timezone for the event section
    const localStartDateTime = startDateTime.toISOString().replace('Z', '');
    const localEndDateTime = endDateTime.toISOString().replace('Z', '');

    // Set search params to trigger the query - use local timezone strings
    setSearchParams({
      startDateTime: localStartDateTime,
      endDateTime: localEndDateTime
    });

    // Close the date range selector modal after search
    setIsDateRangeSelectorModalOpen(false);

    // Track search event
    analytics.trackFilter('date_filter', `${startDate.toISOString()}_${endDate.toISOString()}`, {
      start_date: localStartDateTime,
      end_date: localEndDateTime
    });
  };

  // Loading state
  if (configLoading || translationsLoading || section === null) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations || !section) {
    analytics.trackError('section_load_error', itemsError || new Error('Missing configuration'), {
      section_id: section?.id,
      section_type: 'event'
    });
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">
          {itemsError instanceof Error ? itemsError.message : 'Erreur lors du chargement des données'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary 
      componentName="EventSection" 
      sectionId={section.id}
      sectionName={section.names.fr}
    >
      <div 
        className="min-h-screen"
        style={{ 
          backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
        }}
      >
        <SectionHeader
          config={config}
          title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
          onBackClick={handleBackClick}
          translations={translations}
        />

        {/* Filter */}
        <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
          <SearchAndFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onFiltersOpen={() => setIsFilterDrawerOpen(true)}
            activeFiltersCount={activeFiltersCount}
            config={config}
            translations={translations}
          />
        </div>

        {/* Main content */}
        <main className="main-content">
          <div className="pt-4">
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onSearch={handleSearch}
              config={config}
              translations={translations}
              isModalOpen={isDateRangeSelectorModalOpen}
              setIsModalOpen={setIsDateRangeSelectorModalOpen}
            />
          </div>

          {searchParams === null ? (
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-gray-500">
              <p className="text-center mb-4">
                Veuillez sélectionner une période pour voir les événements
              </p>
            </div>
          ) : itemsIsError ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500 text-center p-4">
              <p className="font-medium mb-2">Erreur lors du chargement</p>
              <p className="text-sm text-gray-500">{itemsError instanceof Error ? itemsError.message : 'Erreur inconnue'}</p>
              <button
                onClick={() => {
                  setSearchParams(prev => prev ? { ...prev } : null);
                }}
                className="mt-4 px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: config['major-color'] }}
              >
                Réessayer
              </button>
            </div>
          ) : itemsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingIndicator />
            </div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500 text-center">
              <p>Aucun événement disponible pour cette période</p>
            </div>
          ) : view === 'list' ? (
            <>
              <ListView
                events={filteredItems}
                onEventClick={handleItemClick}
                onCategorySelect={handleCategorySelect}
                onTypeSelect={handleTypeSelect}
                config={config}
                translations={translations}
                categoryColors={categoryColors}
              />
            </>
          ) : (
            <div 
              className="fixed inset-0 pt-[94px] pb-[40px]"
              style={{ zIndex: 1 }}
            >
              <MapView
                places={places}
                config={config}
                translations={translations}
                onItemClick={handleItemClick}
                onPlaceClick={handlePlaceClick}
                mainLocation={mainLocation}
                categoryColors={categoryColors}
                onCategorySelect={handleCategorySelect}
                onTypeSelect={handleTypeSelect}
                mapInstanceRef={mapInstanceRef}
                activeCategories={activeCategories}
                allCategories={allCategories}
              />
            </div>
          )}
        </main>

        {/* Fixed view toggle button */}
        <div 
          className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
        >
          <ViewToggle
            view={view}
            onChange={handleViewChange}
            config={config}
            translations={translations}
            sectionType="event"
          />
        </div>

        {/* Modals */}
        {selectedPlace && !selectedItem && (
          <PlaceCard
            place={selectedPlace}
            onClose={() => {
              analytics.trackEvent('interaction', 'place_card_close', {
                place_name: selectedPlace.name,
                items_count: selectedPlace.items?.length || selectedPlace.shows?.length
              });
              setSelectedPlace(null);
            }}
            onEventClick={handleEventFromPlace}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
            selectedDate=""
          />
        )}

        {selectedItem && (
          <DetailCard
            item={selectedItem}
            items={filteredItems}
            onClose={handleDetailClose}
            onItemChange={handleItemChange}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            tableId={section.id || ''}
            categoryColors={categoryColors}
            behavior={detailBehavior}
            showIndex={selectedShowIndex}
            place={selectedPlace}
            onShowPlace={(place) => {
              setSelectedItem(null);
              setSelectedPlace(place);
            }}
          />
        )}

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          items={items}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
        />
      </div>
    </ErrorBoundary>
  );
};

export default EventSection;
export { EventSection };